import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Save, RefreshCw, Edit, Eye, Check, X, 
  FileText, User, Briefcase, Calendar, AlertTriangle,
  Copy, RotateCcw, Sparkles, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../services/api';

// Available template variables for each template type
const TEMPLATE_VARIABLES = {
  worker_welcome: [
    { var: '{worker_name}', desc: 'Worker full name' },
    { var: '{email}', desc: 'Worker email address' },
    { var: '{temp_password}', desc: 'Temporary password' },
    { var: '{employee_id}', desc: 'Employee ID (e.g., CC-0001)' },
    { var: '{login_url}', desc: 'Login page URL' },
    { var: '{business_name}', desc: 'Your business name' },
  ],
  booking_confirmation: [
    { var: '{customer_name}', desc: 'Customer name' },
    { var: '{booking_reference}', desc: 'Booking reference (NCH-XXXXXX)' },
    { var: '{service_name}', desc: 'Service booked' },
    { var: '{scheduled_date}', desc: 'Booking date' },
    { var: '{scheduled_time}', desc: 'Booking time' },
    { var: '{address}', desc: 'Service address' },
    { var: '{worker_name}', desc: 'Assigned worker' },
    { var: '{total_amount}', desc: 'Total price' },
    { var: '{business_name}', desc: 'Your business name' },
    { var: '{business_phone}', desc: 'Your business phone' },
  ],
  booking_reminder_customer: [
    { var: '{customer_name}', desc: 'Customer name' },
    { var: '{booking_reference}', desc: 'Booking reference' },
    { var: '{service_name}', desc: 'Service booked' },
    { var: '{scheduled_date}', desc: 'Booking date' },
    { var: '{scheduled_time}', desc: 'Booking time' },
    { var: '{address}', desc: 'Service address' },
    { var: '{worker_name}', desc: 'Assigned worker' },
    { var: '{hours_until}', desc: 'Hours until appointment' },
    { var: '{business_name}', desc: 'Your business name' },
  ],
  booking_reminder_worker: [
    { var: '{worker_name}', desc: 'Worker name' },
    { var: '{booking_reference}', desc: 'Booking reference' },
    { var: '{service_name}', desc: 'Service type' },
    { var: '{customer_name}', desc: 'Customer name' },
    { var: '{scheduled_date}', desc: 'Job date' },
    { var: '{scheduled_time}', desc: 'Job time' },
    { var: '{address}', desc: 'Service address' },
    { var: '{hours_until}', desc: 'Hours until job' },
    { var: '{business_name}', desc: 'Your business name' },
  ],
  worker_assignment: [
    { var: '{worker_name}', desc: 'Worker name' },
    { var: '{booking_reference}', desc: 'Booking reference' },
    { var: '{service_name}', desc: 'Service type' },
    { var: '{customer_name}', desc: 'Customer name' },
    { var: '{scheduled_date}', desc: 'Job date' },
    { var: '{scheduled_time}', desc: 'Job time' },
    { var: '{address}', desc: 'Service address' },
    { var: '{special_instructions}', desc: 'Special instructions' },
    { var: '{estimated_hours}', desc: 'Estimated job duration' },
    { var: '{business_name}', desc: 'Your business name' },
  ],
  payment_reminder: [
    { var: '{customer_name}', desc: 'Customer name' },
    { var: '{booking_reference}', desc: 'Booking reference' },
    { var: '{service_name}', desc: 'Service name' },
    { var: '{amount_due}', desc: 'Amount owed' },
    { var: '{due_date}', desc: 'Due date' },
    { var: '{payment_link}', desc: 'Payment link' },
    { var: '{business_name}', desc: 'Your business name' },
    { var: '{business_phone}', desc: 'Your business phone' },
  ],
  job_completed: [
    { var: '{customer_name}', desc: 'Customer name' },
    { var: '{booking_reference}', desc: 'Booking reference' },
    { var: '{service_name}', desc: 'Service completed' },
    { var: '{worker_name}', desc: 'Worker who completed' },
    { var: '{completion_date}', desc: 'Completion date' },
    { var: '{total_amount}', desc: 'Total amount' },
    { var: '{review_link}', desc: 'Link to leave review' },
    { var: '{business_name}', desc: 'Your business name' },
  ],
  offer_letter: [
    { var: '{worker_name}', desc: 'Worker full name' },
    { var: '{hourly_rate}', desc: 'Offered hourly rate' },
    { var: '{offer_link}', desc: 'Link to accept offer' },
    { var: '{expiry_date}', desc: 'Offer expiration date' },
    { var: '{business_name}', desc: 'Your business name' },
  ],
  password_reset: [
    { var: '{user_name}', desc: 'User name' },
    { var: '{reset_link}', desc: 'Password reset link' },
    { var: '{expiry_time}', desc: 'Link expiration time' },
    { var: '{business_name}', desc: 'Your business name' },
  ],
};

// Default templates
const DEFAULT_TEMPLATES = {
  worker_welcome: {
    subject: '🎉 Welcome to {business_name} - Your Login Credentials',
    body: `Hi {worker_name},

You've been added as a worker at {business_name}! We're excited to have you on the team.

YOUR LOGIN CREDENTIALS:
• Email: {email}
• Temporary Password: {temp_password}
• Employee ID: {employee_id}

⚠️ You will be required to change your password on first login.

NEXT STEPS:
1. Login at {login_url}
2. Change your password to something secure
3. Complete your onboarding documents
4. Set your availability in the app
5. Start accepting jobs!

If you have any questions, please contact the owner.

Welcome aboard! 🚀

- {business_name} Team`
  },
  booking_confirmation: {
    subject: '✅ Booking Confirmed - {service_name}',
    body: `Hi {customer_name},

Your booking has been confirmed!

BOOKING DETAILS:
• Service: {service_name}
• Date: {scheduled_date}
• Time: {scheduled_time}
• Address: {address}
• Worker: {worker_name}
• Total: {total_amount}

We look forward to serving you!

- {business_name} Team`
  },
  offer_letter: {
    subject: '📋 Job Offer from {business_name}',
    body: `Hi {worker_name},

We're pleased to offer you a position at {business_name}!

OFFER DETAILS:
• Hourly Rate: {hourly_rate}
• Offer Expires: {expiry_date}

To accept this offer and begin onboarding, please click the link below:
{offer_link}

If you have any questions, please don't hesitate to reach out.

We hope to welcome you to our team!

- {business_name} Team`
  },
  password_reset: {
    subject: '🔐 Password Reset Request - {business_name}',
    body: `Hi {user_name},

We received a request to reset your password.

Click the link below to reset your password:
{reset_link}

This link will expire in {expiry_time}.

If you didn't request this, please ignore this email.

- {business_name} Team`
  },
  booking_reminder_customer: {
    subject: '⏰ Reminder: Your Service Tomorrow - {booking_reference}',
    body: `Hi {customer_name},

This is a friendly reminder about your upcoming service appointment!

APPOINTMENT DETAILS:
• Reference: {booking_reference}
• Service: {service_name}
• Date: {scheduled_date}
• Time: {scheduled_time}
• Address: {address}
• Worker: {worker_name}

Please ensure someone is available at the service address. If you need to reschedule, please contact us as soon as possible.

See you soon!

- {business_name} Team`
  },
  booking_reminder_worker: {
    subject: '⏰ Job Reminder: {service_name} Tomorrow - {booking_reference}',
    body: `Hi {worker_name},

Reminder about your upcoming job tomorrow:

JOB DETAILS:
• Reference: {booking_reference}
• Service: {service_name}
• Customer: {customer_name}
• Date: {scheduled_date}
• Time: {scheduled_time}
• Address: {address}

Please arrive on time and check in via the app when you start. Don't forget to take before/after photos!

Good luck!

- {business_name} Team`
  },
  worker_assignment: {
    subject: '📋 New Job Assigned - {booking_reference}',
    body: `Hi {worker_name},

You've been assigned a new job!

JOB DETAILS:
• Reference: {booking_reference}
• Service: {service_name}
• Customer: {customer_name}
• Date: {scheduled_date}
• Time: {scheduled_time}
• Address: {address}
• Est. Duration: {estimated_hours}

SPECIAL INSTRUCTIONS:
{special_instructions}

Please log into your dashboard for more details and to confirm the assignment.

- {business_name} Team`
  },
  payment_reminder: {
    subject: '💳 Payment Reminder - {booking_reference}',
    body: `Hi {customer_name},

This is a friendly reminder about your pending payment:

PAYMENT DETAILS:
• Reference: {booking_reference}
• Service: {service_name}
• Amount Due: {amount_due}
• Due Date: {due_date}

Please click the link below to make your payment:
{payment_link}

If you've already made this payment, please disregard this message.

Questions? Call us at {business_phone}

- {business_name} Team`
  },
  job_completed: {
    subject: '✅ Service Completed - {booking_reference}',
    body: `Hi {customer_name},

Your service has been completed!

SUMMARY:
• Reference: {booking_reference}
• Service: {service_name}
• Completed By: {worker_name}
• Date: {completion_date}
• Total: {total_amount}

We hope you're satisfied with our service! Please take a moment to leave us a review:
{review_link}

Your feedback helps us improve and helps other customers find quality service.

Thank you for choosing {business_name}!

- {business_name} Team`
  }
};

const TEMPLATE_INFO = {
  worker_welcome: {
    name: 'Worker Welcome Email',
    description: 'Sent when a new worker is added manually',
    icon: User,
    color: 'text-green-500',
    category: 'worker'
  },
  booking_confirmation: {
    name: 'Booking Confirmation',
    description: 'Sent to customers when booking is confirmed',
    icon: Calendar,
    color: 'text-blue-500',
    category: 'customer'
  },
  booking_reminder_customer: {
    name: 'Customer Reminder',
    description: 'Sent to customers 24 hours before their appointment',
    icon: Clock,
    color: 'text-cyan-500',
    category: 'customer'
  },
  booking_reminder_worker: {
    name: 'Worker Job Reminder',
    description: 'Sent to workers 24 hours before their scheduled job',
    icon: Clock,
    color: 'text-indigo-500',
    category: 'worker'
  },
  worker_assignment: {
    name: 'Job Assignment',
    description: 'Sent to workers when assigned to a new job',
    icon: Briefcase,
    color: 'text-purple-500',
    category: 'worker'
  },
  payment_reminder: {
    name: 'Payment Reminder',
    description: 'Sent to customers with pending payments',
    icon: FileText,
    color: 'text-amber-500',
    category: 'customer'
  },
  job_completed: {
    name: 'Job Completed',
    description: 'Sent to customers when their service is completed',
    icon: Check,
    color: 'text-emerald-500',
    category: 'customer'
  },
  offer_letter: {
    name: 'Offer Letter',
    description: 'Sent to applicants with job offers',
    icon: Briefcase,
    color: 'text-rose-500',
    category: 'worker'
  },
  password_reset: {
    name: 'Password Reset',
    description: 'Sent when user requests password reset',
    icon: AlertTriangle,
    color: 'text-orange-500',
    category: 'system'
  }
};

// Group templates by category
const TEMPLATE_CATEGORIES = {
  customer: { label: 'Customer Notifications', icon: User, color: 'bg-blue-500/10 text-blue-500' },
  worker: { label: 'Worker Notifications', icon: Briefcase, color: 'bg-purple-500/10 text-purple-500' },
  system: { label: 'System Emails', icon: AlertTriangle, color: 'bg-amber-500/10 text-amber-500' }
};

const EmailTemplatesTab = () => {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('worker_welcome');
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplate && templates[selectedTemplate]) {
      setEditedSubject(templates[selectedTemplate].subject || DEFAULT_TEMPLATES[selectedTemplate]?.subject || '');
      setEditedBody(templates[selectedTemplate].body || DEFAULT_TEMPLATES[selectedTemplate]?.body || '');
    } else if (selectedTemplate) {
      setEditedSubject(DEFAULT_TEMPLATES[selectedTemplate]?.subject || '');
      setEditedBody(DEFAULT_TEMPLATES[selectedTemplate]?.body || '');
    }
  }, [selectedTemplate, templates]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/email-templates');
      setTemplates(response.data.templates || {});
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      // Use defaults if fetch fails
      setTemplates({});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/settings/email-templates/${selectedTemplate}`, {
        subject: editedSubject,
        body: editedBody
      });
      
      setTemplates(prev => ({
        ...prev,
        [selectedTemplate]: { subject: editedSubject, body: editedBody }
      }));
      
      toast.success('Template saved!');
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this template to default? Your customizations will be lost.')) return;
    
    try {
      await api.delete(`/settings/email-templates/${selectedTemplate}`);
      
      setTemplates(prev => {
        const newTemplates = { ...prev };
        delete newTemplates[selectedTemplate];
        return newTemplates;
      });
      
      setEditedSubject(DEFAULT_TEMPLATES[selectedTemplate]?.subject || '');
      setEditedBody(DEFAULT_TEMPLATES[selectedTemplate]?.body || '');
      
      toast.success('Template reset to default');
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const insertVariable = (variable) => {
    // Insert at cursor position or append
    setEditedBody(prev => prev + ' ' + variable);
    toast.success(`Inserted ${variable}`);
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.success(`Copied ${variable}`);
  };

  const getPreviewContent = (content) => {
    // Replace variables with sample data for preview
    const sampleData = {
      '{worker_name}': 'John Smith',
      '{email}': 'john.smith@example.com',
      '{temp_password}': 'abc12345',
      '{employee_id}': 'CC-0001',
      '{login_url}': 'https://nchelphub.com/login',
      '{business_name}': 'NC Help Hub',
      '{business_phone}': '(252) 555-0001',
      '{customer_name}': 'Jane Doe',
      '{booking_reference}': 'NCH-123456',
      '{service_name}': 'Grocery Pickup',
      '{scheduled_date}': 'December 25, 2025',
      '{scheduled_time}': '10:00 AM',
      '{address}': '123 Main St, Tarboro, NC 27886',
      '{total_amount}': '$150.00',
      '{hourly_rate}': '$25/hour',
      '{offer_link}': 'https://nchelphub.com/offer/abc123',
      '{expiry_date}': 'December 30, 2025',
      '{user_name}': 'John Smith',
      '{reset_link}': 'https://nchelphub.com/reset/xyz789',
      '{expiry_time}': '1 hour',
      '{hours_until}': '24',
      '{special_instructions}': 'Use eco-friendly products. Pet cat named Whiskers.',
      '{estimated_hours}': '3 hours',
      '{amount_due}': '$150.00',
      '{due_date}': 'December 30, 2025',
      '{payment_link}': 'https://nchelphub.com/pay/abc123',
      '{completion_date}': 'December 25, 2025',
      '{review_link}': 'https://nchelphub.com/review/abc123',
    };
    
    let preview = content;
    Object.entries(sampleData).forEach(([key, value]) => {
      preview = preview.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    return preview;
  };

  const currentTemplate = templates[selectedTemplate] || DEFAULT_TEMPLATES[selectedTemplate];
  const templateInfo = TEMPLATE_INFO[selectedTemplate];
  const TemplateIcon = templateInfo?.icon || Mail;
  const isCustomized = templates[selectedTemplate] !== undefined;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-[var(--primary)]" />
            Email Templates
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Customize the emails sent to workers and customers
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Template Selector - Grouped by Category */}
        <div className="lg:col-span-1 space-y-4">
          {Object.entries(TEMPLATE_CATEGORIES).map(([category, catInfo]) => {
            const CatIcon = catInfo.icon;
            const templatesInCategory = Object.entries(TEMPLATE_INFO).filter(([_, info]) => info.category === category);
            if (templatesInCategory.length === 0) return null;
            
            return (
              <div key={category}>
                <div className={`flex items-center gap-2 px-2 py-1 rounded-lg ${catInfo.color} mb-2`}>
                  <CatIcon className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase">{catInfo.label}</span>
                </div>
                <div className="space-y-1">
                  {templatesInCategory.map(([key, info]) => {
                    const Icon = info.icon;
                    const isSelected = selectedTemplate === key;
                    const hasCustom = templates[key] !== undefined;
                    return (
                      <button
                        key={key}
                        onClick={() => { setSelectedTemplate(key); setEditMode(false); setPreviewMode(false); }}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          isSelected 
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                            : 'border-[var(--border-primary)] hover:border-[#D4AF37]/50'
                        }`}
                        data-testid={`template-${key}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`w-5 h-5 ${info.color}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">{info.name}</span>
                              {hasCustom && (
                                <span className="text-xs px-1.5 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] rounded">Custom</span>
                              )}
                            </div>
                            <p className="text-xs text-[var(--text-muted)] truncate">{info.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-3">
          <div className="card">
            {/* Template Header */}
            <div className="p-4 border-b border-[var(--border-primary)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TemplateIcon className={`w-6 h-6 ${templateInfo?.color}`} />
                <div>
                  <h3 className="font-semibold">{templateInfo?.name}</h3>
                  <p className="text-sm text-[var(--text-muted)]">{templateInfo?.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isCustomized && !editMode && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleReset}
                    className="text-[var(--text-muted)]"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Reset
                  </Button>
                )}
                {!editMode ? (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPreviewMode(!previewMode)}
                      data-testid="preview-btn"
                    >
                      <Eye className="w-4 h-4 mr-1" /> {previewMode ? 'Hide Preview' : 'Preview'}
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => setEditMode(true)}
                      className="btn-primary"
                      data-testid="edit-template-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => { setEditMode(false); setEditedSubject(currentTemplate?.subject || ''); setEditedBody(currentTemplate?.body || ''); }}
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={handleSave}
                      disabled={saving}
                      className="btn-primary"
                      data-testid="save-template-btn"
                    >
                      <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Template Content */}
            <div className="p-4">
              {editMode ? (
                <div className="space-y-4">
                  {/* Subject */}
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-1 block">Subject Line</label>
                    <Input
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      placeholder="Email subject..."
                      className="bg-[var(--background)] text-white border-[var(--border-primary)]"
                      data-testid="template-subject-input"
                    />
                  </div>

                  {/* Variables */}
                  <div className="p-3 bg-[var(--background-subtle)] rounded-lg">
                    <p className="text-sm font-medium text-[var(--text-muted)] mb-2 flex items-center gap-1">
                      <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                      Available Variables (click to insert)
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {TEMPLATE_VARIABLES[selectedTemplate]?.map(({ var: v, desc }) => (
                        <button
                          key={v}
                          onClick={() => insertVariable(v)}
                          className="group flex items-center gap-1 px-2 py-1 bg-[var(--background-paper)] rounded border border-[var(--border-primary)] hover:border-[#D4AF37] transition-colors"
                          title={desc}
                        >
                          <code className="text-xs text-[#D4AF37]">{v}</code>
                          <Copy 
                            className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); copyVariable(v); }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Body */}
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-1 block">Email Body</label>
                    <Textarea
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      placeholder="Email content..."
                      rows={15}
                      className="font-mono text-sm bg-[var(--background)] text-white border-[var(--border-primary)]"
                      data-testid="template-body-input"
                    />
                  </div>
                </div>
              ) : previewMode ? (
                <div className="space-y-4">
                  <div className="p-3 bg-[var(--info)]/10 border border-[var(--info)]/30 rounded-lg">
                    <p className="text-sm text-blue-700 flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Preview Mode - Variables are replaced with sample data
                    </p>
                  </div>
                  
                  <div className="border border-[var(--border-primary)] rounded-lg overflow-hidden">
                    <div className="p-3 bg-[var(--background-subtle)] border-b border-[var(--border-primary)]">
                      <p className="text-sm"><span className="text-[var(--text-muted)]">Subject:</span> <span className="font-medium">{getPreviewContent(editedSubject)}</span></p>
                    </div>
                    <div className="p-4 bg-[#1a1a1a] min-h-[300px]">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-white">
                        {getPreviewContent(editedBody)}
                      </pre>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-1 block">Subject Line</label>
                    <div className="p-3 bg-[var(--background-subtle)] rounded-lg">
                      <p className="font-medium text-white">{currentTemplate?.subject || 'No subject set'}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-1 block">Email Body</label>
                    <div className="p-4 bg-[var(--background-subtle)] rounded-lg min-h-[200px]">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-white">
                        {currentTemplate?.body || 'No content set'}
                      </pre>
                    </div>
                  </div>

                  {/* Variables Reference */}
                  <div className="p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
                    <p className="text-sm font-medium text-[#D4AF37] mb-2">Available Variables</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {TEMPLATE_VARIABLES[selectedTemplate]?.map(({ var: v, desc }) => (
                        <div key={v} className="text-xs">
                          <code className="text-[#D4AF37]">{v}</code>
                          <span className="text-[var(--text-muted)]"> - {desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EmailTemplatesTab;
